// Standard Excel indexed palette (first 64). Many files use this for older styles.
export const EXCEL_INDEXED_COLORS = [
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "800000", "008000", "000080", "808000", "800080", "008080", "C0C0C0", "808080",
  "9999FF", "993366", "FFFFCC", "CCFFFF", "660066", "FF8080", "0066CC", "CCCCFF",
  "000080", "FF00FF", "FFFF00", "00FFFF", "800080", "800000", "008080", "0000FF",
  "00CCFF", "CCFFFF", "CCFFCC", "FFFF99", "99CCFF", "FF99CC", "CC99FF", "FFCC99",
  "3366FF", "33CCCC", "99CC00", "FFCC00", "FF9900", "FF6600", "666699", "969696",
  "003366", "339966", "003300", "333300", "993300", "993366", "333399", "333333",
];

export function rgbToCss(rgb) {
  if (!rgb || typeof rgb !== "string") return undefined;
  const hex = rgb.length === 8 ? rgb.slice(2) : rgb;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return `#${hex}`;
}

export function argbToCss(argb) {
  if (!argb || typeof argb !== "string") return undefined;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return `#${hex}`;
}

export function clampByte(n) {
  return Math.max(0, Math.min(255, n));
}

/**
 * Apply an Excel colour tint (-1..1) to a hex colour.
 *
 * Excel defines tint on the HLS luminance channel (ECMA-376 §18.8.19):
 *   tint < 0 : L' = L * (1 + tint)
 *   tint > 0 : L' = L * (1 - tint) + (1 - (1 - tint))
 * Blending the RGB channels toward white/black instead (the previous
 * implementation) visibly shifts theme colours used by Excel templates.
 */
export function applyTintToHex(hex, tint) {
  if (typeof tint !== "number" || Number.isNaN(tint) || tint === 0) return hex;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  if ([r, g, b].some((v) => Number.isNaN(v))) return hex;

  // RGB -> HLS
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }

  const t = Math.max(-1, Math.min(1, tint));
  l = t < 0 ? l * (1 + t) : l * (1 - t) + t;

  // HLS -> RGB
  const hue2rgb = (p, q, x) => {
    let k = x;
    if (k < 0) k += 1;
    if (k > 1) k -= 1;
    if (k < 1 / 6) return p + (q - p) * 6 * k;
    if (k < 1 / 2) return q;
    if (k < 2 / 3) return p + (q - p) * (2 / 3 - k) * 6;
    return p;
  };
  let rr; let gg; let bb;
  if (s === 0) {
    rr = gg = bb = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rr = hue2rgb(p, q, h + 1 / 3);
    gg = hue2rgb(p, q, h);
    bb = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v) => clampByte(Math.round(v * 255)).toString(16).padStart(2, "0").toUpperCase();
  return `${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
}

export function resolveExcelColorToCss(color, themeColors) {
  if (!color || typeof color !== "object") return undefined;

  // exceljs sometimes provides argb directly
  if (color.argb) return argbToCss(String(color.argb));

  // theme-based color
  if (typeof color.theme === "number" && Array.isArray(themeColors)) {
    const base = themeColors[color.theme];
    if (base) {
      const tinted = applyTintToHex(base, typeof color.tint === "number" ? color.tint : 0);
      return `#${tinted}`;
    }
  }

  // indexed palette
  if (typeof color.indexed === "number") {
    const base = EXCEL_INDEXED_COLORS[color.indexed];
    if (base) return `#${base}`;
  }

  // rgb (rare)
  if (color.rgb) return rgbToCss(String(color.rgb));

  return undefined;
}
